import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, MessageSquare, Globe } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";

export default function NavigationHeader() {
  const { t, lang, toggle } = useLang();
  const settings = useSettings();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const links = [
    { to: "/pax-montering", label: t.nav.pax },
    { to: "/oevrige-moebler", label: t.nav.other },
    { to: "/ikea-koekken", label: t.nav.kitchen },
    { to: "/serviceomraade", label: t.nav.area },
  ];

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-stone-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-home-link">
            <img src="/logo-header.png" alt="Belov Multiservice" className="hidden sm:block h-9 md:h-11 w-auto" />
            <img src="/logo-icon.png" alt="" className="sm:hidden w-9 h-9" />
            <span className="sm:hidden font-display font-extrabold text-base tracking-tight text-[#1E3A2F]">
              {settings.company?.name || "Belov Multiservice"}
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="text-sm font-medium text-stone-700 hover:text-[#1E3A2F] transition-colors"
                data-testid={`nav-link-${l.to.replace("/", "")}`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={toggle}
              className="flex items-center gap-1.5 text-sm font-semibold text-stone-600 hover:text-[#1E3A2F] px-2 py-1 rounded-md"
              data-testid="lang-toggle-btn"
              aria-label="Toggle language"
            >
              <Globe className="w-4 h-4" />
              {lang === "da" ? "EN" : "DA"}
            </button>
            <a
              href={`sms:${(settings.company?.phone || "").replace(/\s/g, "")}`}
              className="hidden md:flex items-center gap-1.5 text-sm font-semibold text-stone-700 hover:text-[#1E3A2F]"
              data-testid="header-sms-link"
              title={t.contact?.sms}
            >
              <MessageSquare className="w-4 h-4" />
              {settings.company?.phone}
            </a>
            <button
              onClick={() => navigate("/pax-montering")}
              className="hidden sm:inline-flex items-center rounded-full bg-[#1E3A2F] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#162D24] transition-colors"
              data-testid="header-cta-btn"
            >
              {t.nav.cta}
            </button>
            <button
              className="lg:hidden p-2 text-stone-700"
              onClick={() => setOpen((o) => !o)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-stone-200 bg-white" data-testid="mobile-menu">
          <div className="px-4 py-3 space-y-1">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="block px-3 py-3 rounded-lg text-base font-medium text-stone-800 hover:bg-stone-50"
                data-testid={`mobile-nav-${l.to.replace("/", "")}`}
              >
                {l.label}
              </Link>
            ))}
            <button
              onClick={() => { setOpen(false); navigate("/pax-montering"); }}
              className="w-full mt-2 rounded-full bg-[#1E3A2F] text-white px-5 py-3 text-base font-semibold"
              data-testid="mobile-cta-btn"
            >
              {t.nav.cta}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
